import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiBookPlusOutline, mdiLightbulbOnOutline, mdiLoading } from '@mdi/js'
import { useCan } from '../../contexts/PermissionsContext'
import {
    useDecideKnowledgeProposal,
    useKnowledgeDraft,
    useKnowledgeProposal,
    useRequestKnowledgeDraft,
} from '../../hooks/useKnowledge'
import type { Conversation } from '../../api/schema'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import {
    KnowledgeProposalDialog,
    KnowledgeProposalReviewDialog,
    type ProposalSeed,
    type ProposalTarget,
} from '../knowledge/KnowledgeProposalDialog'

/**
 * *Save as knowledge* on a sent reply (spec 2026-11-01-01): queues the draft
 * job, spins while it runs, then opens the dialog on the draft. A failed or
 * capped job opens it empty with the reason, so the manager can still write
 * the note by hand.
 */
export function SaveAsKnowledgeButton({
    org,
    conversation,
    messageUid,
}: {
    org: string
    conversation: Conversation
    messageUid: string
}) {
    const { t } = useTranslation('knowledge')
    const canRead = useCan('knowledge.read')
    const request = useRequestKnowledgeDraft(org)
    const [jobUid, setJobUid] = useState<string | null>(null)
    const [open, setOpen] = useState(false)
    const { data: status, isError } = useKnowledgeDraft(org, open ? jobUid : null)
    const pending = request.isPending || (!!jobUid && !isError && (!status || status.status === 'pending'))

    const anchorUid = conversation.asset?.uid ? String(conversation.asset.uid) : undefined
    const anchorName = String(conversation.asset?.name ?? '')
    const draft = status?.status === 'done' ? status.draft : undefined
    const targets = useMemo(() => {
        const out: ProposalTarget[] = []
        if (anchorUid) out.push({ type: 'asset', uid: anchorUid, label: t('proposals.target_anchor', { name: anchorName }) })
        if (draft?.site_asset_uid && draft.site_asset_uid !== anchorUid) {
            out.push({ type: 'asset', uid: draft.site_asset_uid, label: t('proposals.target_site', { name: draft.site_asset_name ?? '' }) })
        }
        out.push({ type: 'organization', label: t('proposals.target_organization') })

        return out
    }, [anchorUid, anchorName, draft?.site_asset_uid, draft?.site_asset_name, t])

    const finished = !pending && (status || isError || request.isError)
    const seed = useMemo<ProposalSeed | null>(() => {
        if (!finished) return null
        const base = { conversation_uid: String(conversation.uid), message_uid: messageUid }
        if (!draft) return { action: 'create', ...base }

        return {
            ...base,
            action: draft.action,
            page_uid: draft.page_uid,
            target_entity_type: draft.target_entity_type,
            target_entity_uid: draft.target_entity_uid,
            kind: draft.kind,
            title: draft.title,
            content: draft.content,
            applies_to_subtree: draft.applies_to_subtree,
            rationale: draft.rationale,
            current_text: draft.current_text,
            current_title: draft.current_title,
        }
    }, [finished, draft, conversation.uid, messageUid])
    const notice =
        status?.error === 'ai_budget_reached'
            ? t('proposals.draft_budget')
            : finished && !draft
              ? t('proposals.draft_failed')
              : null

    useEffect(() => {
        if (!open) setJobUid(null)
    }, [open])

    if (!canRead) return null

    const start = (event: React.MouseEvent) => {
        // The header toggles the message's focus; this is not that.
        event.stopPropagation()
        setOpen(true)
        request.mutate(
            { conversation_uid: String(conversation.uid), message_uid: messageUid },
            { onSuccess: (uid) => setJobUid(uid) },
        )
    }

    return (
        <>
            <button
                type="button"
                onClick={start}
                disabled={pending}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                aria-label={t('save_as_knowledge')}
                title={t('save_as_knowledge')}
                data-testid="message-save-knowledge"
            >
                <AppIcon path={pending ? mdiLoading : mdiBookPlusOutline} size="xs" className={pending ? 'motion-safe:animate-spin' : undefined} />
            </button>
            <KnowledgeProposalDialog
                org={org}
                open={open}
                onOpenChange={setOpen}
                seed={seed}
                targets={targets}
                personalDataHits={status?.personal_data_hits}
                loading={pending}
                notice={notice}
            />
        </>
    )
}

/**
 * « Remember for next time? » under the reply a suggestion came from (spec
 * 2026-11-01-01), for a reader who may decide it: *Review* opens the proposal,
 * *Dismiss* closes it.
 */
export function KnowledgeSuggestionCard({ org, proposalUid, title }: { org: string; proposalUid: string; title: string }) {
    const { t } = useTranslation('knowledge')
    const canWrite = useCan('knowledge.write')
    const [reviewing, setReviewing] = useState(false)
    const { data: proposal } = useKnowledgeProposal(org, reviewing ? proposalUid : undefined)
    const dismiss = useDecideKnowledgeProposal(org, 'dismiss')
    if (!canWrite) return null

    return (
        <div
            className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 p-2"
            data-testid="knowledge-suggestion-card"
        >
            <div className="flex min-w-0 items-center gap-1.5 text-xs">
                <AppIcon path={mdiLightbulbOnOutline} size="xs" className="shrink-0 text-primary" aria-hidden />
                <span className="font-medium">{t('suggestion.title')}</span>
                <span className="truncate text-muted-foreground">{title}</span>
            </div>
            <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7" onClick={() => setReviewing(true)} data-testid="knowledge-suggestion-review">
                    {t('suggestion.review')}
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-7"
                    disabled={dismiss.isPending}
                    onClick={() => dismiss.mutate({ uid: proposalUid })}
                    data-testid="knowledge-suggestion-dismiss"
                >
                    {t('suggestion.dismiss')}
                </Button>
            </div>
            {reviewing && <KnowledgeProposalReviewDialog org={org} proposal={proposal ?? null} onClose={() => setReviewing(false)} />}
        </div>
    )
}
